'use client'
import {
  BookingType_default,
  Deal_default,
  Location_default,
  Poster_default,
  Price_default,
  Type_default
} from "./chunk-X26Q276J.mjs";
import {
  ContentfulMedia_default
} from "./chunk-BYRNGPWN.mjs";
import {
  createSectionItemHref,
  parseCardItem,
  resolveContentfulEventType
} from "./chunk-DJNQ4XY4.mjs";

// src/components/product/card/ActionCard.tsx
import { twMerge } from "tailwind-merge";
import { Button, Paper, Typography } from "@wbk/ui";
import { useAnalytics } from "@wbk/analytics";
import { Link, useRouter } from "@wbk/config";
import { formatDate } from "@wbk/utils";
import { jsx, jsxs } from "react/jsx-runtime";
var ActionCard = ({ item, section, index, className }) => {
  var _a, _b;
  const resolvedType = resolveContentfulEventType(item);
  const card = parseCardItem({ item });
  const { trackEvent } = useAnalytics();
  const { locale } = useRouter();
  const startAt = (_a = card == null ? void 0 : card.schedule) == null ? void 0 : _a.openDateTime;
  const endAt = (_b = card == null ? void 0 : card.schedule) == null ? void 0 : _b.closeDateTime;
  const date = startAt && endAt ? `${formatDate(startAt, locale, { month: "short", day: "numeric" })} - ${formatDate(endAt, locale, { month: "short", day: "numeric" })}` : "";
  if (!card) {
    return null;
  }
  return /* @__PURE__ */ jsxs(
    Paper,
    {
      border: true,
      className: twMerge(
        "bg-body border-paper-level-4 flex flex-col items-start overflow-hidden !px-0 !py-0",
        className
      ),
      children: [
        /* @__PURE__ */ jsx(Poster_default, { eventType: card.eventType, className: "aspect-[3/2] rounded-none", children: /* @__PURE__ */ jsx(
          ContentfulMedia_default,
          {
            className: "aspect-[3/2] w-full object-contain",
            media: card.image32 || card.image31 || card.image11,
            width: 900,
            height: 600,
            fit: "thumb",
            placeholder: true
          }
        ) }),
        /* @__PURE__ */ jsxs("div", { className: "px-M py-S space-y-L flex w-full flex-1 flex-col justify-between text-start", children: [
          /* @__PURE__ */ jsxs("div", { className: "space-y-XXS", children: [
            /* @__PURE__ */ jsx(Typography, { variant: "heading-S", className: "line-clamp-2", children: card.title }),
            /* @__PURE__ */ jsx(Typography, { color: "text-secondary", children: date })
          ] }),
          /* @__PURE__ */ jsx(Typography, { color: "text-secondary", variant: "label-M", children: card.subtitle }),
          /* @__PURE__ */ jsxs("div", { className: "space-y-S", children: [
            /* @__PURE__ */ jsx(Price_default, { item: card, priceClassName: "text-heading-S font-bold" }),
            card.buttonLabel && /* @__PURE__ */ jsx(
              Button,
              {
                as: Link,
                "data-testid": `item_${resolvedType}_${card.slug}`,
                to: `/${locale}/${resolvedType}/${card.slug}`,
                className: "w-full",
                onClick: () => {
                  var _a2;
                  trackEvent({
                    event: "clicked_on_section_item",
                    data: {
                      type: card.__typename,
                      item_id: (_a2 = card.sys) == null ? void 0 : _a2.id,
                      slug: card.slug,
                      section: section == null ? void 0 : section.id,
                      position: index
                    }
                  });
                },
                children: /* @__PURE__ */ jsx(Typography, { strong: true, children: card.buttonLabel })
              }
            )
          ] })
        ] })
      ]
    }
  );
};
var ActionCard_default = ActionCard;

// src/components/product/card/ListItem.tsx
import { Button as Button2, ImageWithPlaceholder } from "@wbk/ui";
import { Link as Link2, useRouter as useRouter2 } from "@wbk/config";
import { useAnalytics as useAnalytics2 } from "@wbk/analytics";
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var ProductListItem = ({ item, index, onClick }) => {
  var _a;
  const { locale } = useRouter2();
  const { trackEvent } = useAnalytics2();
  const card = parseCardItem({ item });
  if (!card) {
    return null;
  }
  return /* @__PURE__ */ jsxs2(
    Button2,
    {
      "data-testid": `search_event_button_${card.slug}`,
      as: Link2,
      href: `/${locale}${createSectionItemHref(card)}`,
      onClick: () => {
        trackEvent({
          event: "clicked_on_section_item",
          data: {
            type: card.__typename,
            slug: card.slug,
            section: "list",
            position: index
          }
        });
        if (onClick) onClick();
      },
      className: "w-full justify-start gap-3 p-0.5 text-start",
      theme: "action",
      shape: "text",
      rounded: true,
      children: [
        /* @__PURE__ */ jsx2(
          Poster_default,
          {
            eventType: card.eventType,
            className: "h-[110px] w-[110px] shrink-0 md:h-36 md:w-36",
            size: "sm",
            children: /* @__PURE__ */ jsx2(
              ImageWithPlaceholder,
              {
                src: (_a = card.image11) == null ? void 0 : _a.url,
                className: "h-full w-full rounded-md object-cover"
              }
            )
          }
        ),
        /* @__PURE__ */ jsxs2("div", { className: "space-y-px [&>div:nth-child(1)]:text-xs [&>div:nth-child(3)]:text-sm", children: [
          /* @__PURE__ */ jsx2(Type_default, { item: card }),
          /* @__PURE__ */ jsx2("p", { className: "line-clamp-2 text-sm", children: card.title }),
          /* @__PURE__ */ jsx2(Location_default, { location: card.location, className: "text-text-secondary text-xs" }),
          card.deal && /* @__PURE__ */ jsx2(Deal_default, { item: card, className: "relative m-0 inline-flex md:!my-1" }),
          /* @__PURE__ */ jsx2(Price_default, { item: card }),
          /* @__PURE__ */ jsx2(BookingType_default, { className: "py-0.5", type: card.eventType, mini: true })
        ] })
      ]
    }
  );
};
var ListItem_default = ProductListItem;

// src/components/product/card/MiniItem.tsx
import { Button as Button3, Typography as Typography2 } from "@wbk/ui";
import { Link as Link3, useRouter as useRouter3 } from "@wbk/config";
import { useAnalytics as useAnalytics3 } from "@wbk/analytics";
import { ChevronIcon } from "@wbk/svg";
import { jsx as jsx3, jsxs as jsxs3 } from "react/jsx-runtime";
var ProductMiniItem = ({ item, index, onClick }) => {
  var _a;
  const { locale } = useRouter3();
  const { trackEvent } = useAnalytics3();
  const card = parseCardItem({ item });
  if (!card) {
    return null;
  }
  return /* @__PURE__ */ jsxs3(
    Button3,
    {
      "data-testid": `mini_item_${card.slug}`,
      as: Link3,
      theme: "action",
      shape: "text",
      href: `/${locale}${createSectionItemHref(card)}`,
      className: "gap-S relative flex w-full items-center justify-start p-0 pe-2.5",
      onClick: () => {
        var _a2;
        if (onClick) {
          onClick();
        } else {
          trackEvent({
            event: "clicked_on_section_item",
            data: {
              type: card.__typename,
              item_id: (_a2 = card.sys) == null ? void 0 : _a2.id,
              slug: card.slug,
              section: "list",
              position: index
            }
          });
        }
      },
      children: [
        /* @__PURE__ */ jsx3(
          ContentfulMedia_default,
          {
            media: card.image11,
            poster: (_a = card.image31) == null ? void 0 : _a.url,
            className: "aspect-1 rounded-S h-full w-full max-w-14 shrink-0 overflow-hidden object-cover object-center",
            width: 800,
            height: 800,
            fit: "thumb",
            ratio: "1x1",
            placeholder: true
          }
        ),
        /* @__PURE__ */ jsxs3("div", { className: "space-y-XXS grow p-0.5 text-start", children: [
          /* @__PURE__ */ jsx3(Typography2, { variant: "label-L", className: "line-clamp-2", children: card.title }),
          card.subtitle && /* @__PURE__ */ jsx3(Typography2, { variant: "label-S", color: "text-secondary", className: "line-clamp-1", children: card.subtitle })
        ] }),
        /* @__PURE__ */ jsx3(ChevronIcon, { className: "text-text-secondary shrink-0 rtl:rotate-180" })
      ]
    }
  );
};
var MiniItem_default = ProductMiniItem;

export {
  ActionCard_default,
  ListItem_default,
  MiniItem_default
};
//# sourceMappingURL=chunk-7SNQ645S.mjs.map